/* Header (navy 950) with the right cluster from handoff/03-design/kits/shell-kit.html. */
.header {
  position: relative;
  z-index: 20;
  background: var(--brand-navy-950);
  color: var(--white);
}

.row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
  max-width: var(--container-max);
  min-height: var(--header-height);
  margin: 0 auto;
  padding: 0 var(--content-gutter);
}

.logoLink {
  display: inline-flex;
  align-items: center;
  min-height: var(--control-height);
}

.logo {
  display: block;
  width: auto;
  height: 34px;
}

/* Level 1 */
.nav {
  flex: 1 1 auto;
  min-width: 0;
}

.navList {
  display: flex;
  flex-wrap: wrap;
  gap: 0 24px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.navLink {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: var(--control-height);
  color: var(--text-on-dark-muted);
  font-family: var(--font-ui);
  font-size: 14px;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
  transition: color var(--dur-fast) ease;
}

.navLink:hover {
  color: var(--white);
}

.navLink:is([aria-current='page'], [aria-current='true']) {
  color: var(--white);
}

.navLink:is([aria-current='page'], [aria-current='true'])::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 6px;
  left: 0;
  height: 2px;
  background: var(--brand-cyan);
}

/* Phone: the SHELL1 phone board's Level 1 (13 px, 20 px apart, 8 px between wrapped rows). An
   employee's four items stay on one row down to 360 px; five or more wrap, as the board does, and
   nothing is hidden (QA-018). */
@media (max-width: 600px) {
  .navList {
    gap: 8px 20px;
  }

  .navLink {
    font-size: 13px;
  }
}

/* Right cluster */
.cluster {
  display: flex;
  flex: 100 1 400px;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
  container-type: inline-size;
  container-name: cluster;
}

.clusterButton {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--control-height);
  padding: 0 10px;
  border: 1px solid var(--white-a22);
  border-radius: 4px;
  background: var(--white-a06);
  color: var(--brand-mist-200);
  font: 500 12px/1 var(--font-body);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color var(--dur-fast) ease,
    border-color var(--dur-fast) ease,
    color var(--dur-fast) ease;
}

.clusterButton[data-hovered] {
  border-color: var(--white-a42);
  background: var(--white-a12);
  color: var(--white);
}

.clusterButton svg {
  flex: none;
}

.search {
  flex: 0 3 auto;
  min-width: 44px;
  overflow: hidden;
}

.searchLabel {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kbd {
  flex: none;
  padding: 4px 5px;
  border: 1px solid var(--white-a30);
  border-radius: var(--radius-sm);
  background: var(--white-a04);
  color: var(--gray-100);
  font: 600 11px/1 var(--font-ui);
}

.help {
  flex: none;
  gap: 6px;
  padding: 0 8px;
  border-color: transparent;
  background: transparent;
}

.help[aria-expanded='true'] {
  border-color: var(--brand-cyan);
  background: var(--cyan-a18);
  color: var(--white);
}

.feedback {
  flex: none;
  gap: 6px;
  padding: 0 10px;
  border-color: transparent;
  background: transparent;
}

/* Below 1200 px the button keeps its icon; "Send feedback" stays in its accessible name. */
@media (max-width: 1199px) {
  .feedbackWord {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .feedback {
    min-width: var(--control-height);
    justify-content: center;
  }
}

.bell {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--white);
  cursor: pointer;
}

.bell[data-hovered] {
  background: var(--white-a08);
}

.bellDot {
  position: absolute;
  top: 9px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--brand-gold);
}

.me {
  position: relative;
  display: inline-flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: var(--control-height);
  margin-left: 2px;
  padding: 2px 6px 2px 2px;
  border: 0;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--white);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--dur-fast) ease;
}

.me[data-hovered],
.me[aria-expanded='true'] {
  background: var(--white-a08);
}

.avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font: 500 13px/1 var(--font-ui);
  letter-spacing: 0.02em;
}

.avatarLg {
  width: 44px;
  height: 44px;
  font-size: 15px;
}

.toneGold {
  background: var(--brand-gold);
  color: var(--brand-navy-950);
}

.tonePetrol {
  background: var(--brand-petrol);
  color: var(--white);
}

.toneCyan {
  background: var(--brand-cyan);
  color: var(--brand-navy-950);
}

.toneSky {
  background: var(--brand-sky);
  color: var(--brand-navy-950);
}

.toneNavy {
  background: var(--brand-navy);
  color: var(--white);
}

.who {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  max-width: 178px;
}

.name {
  overflow: hidden;
  color: var(--white);
  font: 600 13px/1.2 var(--font-body);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.roleLine {
  overflow: hidden;
  color: var(--text-on-dark-muted);
  font: 400 11px/1.2 var(--font-body);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.caret {
  flex: none;
  color: var(--text-on-dark-muted);
}

/* Narrow clusters drop the words visually; they stay in each button's accessible name. */
@container cluster (max-width: 575px) {
  .searchLabel {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
}

@container cluster (max-width: 440px) {
  .kbd {
    display: none;
  }

  .helpWord {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .search {
    padding: 0 12px;
  }
}

@container cluster (max-width: 330px) {
  .who {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .caret {
    display: none;
  }

  .me {
    padding: 2px;
  }
}

/* Popovers: account menu and bell */
.popover {
  width: 320px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border-radius: var(--radius-md);
  background: var(--white);
  color: var(--text-body);
  box-shadow: var(--shadow-menu);
}

.popover[data-entering] {
  animation: drop 180ms ease-out;
}

@keyframes drop {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
}

.bellPopover {
  width: 380px;
}

.popoverRule {
  height: 3px;
  background: var(--gradient-rule);
}

.dialog {
  outline: none;
}

.menuHead {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 14px;
  border-bottom: 1px solid var(--border-hairline);
}

.menuName {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.menuNameText {
  margin: 0;
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-bold);
}

/* SET1: self-entered pronouns, beside the name and quieter than it. */
.pronouns {
  color: var(--text-on-dark-muted);
  font-weight: 400;
}

.menuPronouns {
  color: var(--text-secondary);
  font-size: 13px;
}

.menuEmail {
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 13px;
  text-overflow: ellipsis;
}

.roles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 18px 14px;
  border-bottom: 1px solid var(--border-hairline);
}

.sectionLabel {
  margin: 0;
  color: var(--text-muted);
  font-size: 11px;
}

.roleList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.roleItem {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.roleScope {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  outline: none;
}

.menuItem {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: var(--control-height);
  padding: 0 12px;
  border-radius: 4px;
  color: var(--text-body);
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  outline: none;
}

.menuItem svg {
  flex: none;
  color: var(--text-secondary);
}

.menuItem[data-focused],
.menuItem[data-hovered] {
  background: var(--brand-cyan-50);
  color: var(--brand-navy);
}

.menuItem[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.menuItemText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
}

.menuItemSub {
  color: var(--text-secondary);
  font-size: 12px;
}

.signOut {
  margin-top: 4px;
  border-top: 1px solid var(--border-hairline);
  border-radius: 0 0 4px 4px;
}

/* Bell panel */
.bellHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 12px;
  border-bottom: 1px solid var(--border-hairline);
}

.bellTitle {
  color: var(--text-heading);
  font-size: 20px;
  letter-spacing: 0.02em;
}

.bellList {
  display: flex;
  flex-direction: column;
  max-height: 360px;
  margin: 0;
  padding: 8px;
  overflow-y: auto;
  list-style: none;
}

.bellItem {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 4px;
  color: var(--text-body);
  text-decoration: none;
}

.bellItem:hover,
.bellItem[data-hovered] {
  background: var(--brand-cyan-50);
  color: var(--text-body);
}

.bellItemTitle {
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-bold);
  line-height: 1.35;
}

.bellItemMeta {
  color: var(--text-secondary);
  font-size: 12px;
}

.bellFoot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 12px;
  border-top: 1px solid var(--border-hairline);
}

.bellFoot > :last-child {
  margin-left: auto;
}

.bellEmpty {
  padding: 12px;
}

.bellRole {
  align-self: flex-start;
}

.bellHeadText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.bellSummary {
  color: var(--text-secondary);
  font-size: 12px;
}

.bellTextButton {
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--text-link);
  font-family: var(--font-ui);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-decoration: underline;
  text-transform: uppercase;
  cursor: pointer;
}

.bellTextButton[data-focus-visible],
.bellDismiss[data-focus-visible],
.bellItem[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.bellTextButton[data-disabled] {
  color: var(--text-secondary);
  cursor: default;
}

.bellBody {
  max-height: min(420px, 60vh);
  overflow-y: auto;
}

.bellBody .bellList {
  max-height: none;
  overflow: visible;
}

.bellStarted {
  border-bottom: 1px solid var(--border-hairline);
}

.bellSection {
  margin: 12px 18px 0;
  color: var(--text-gold);
}

.bellItemStarted {
  background: var(--surface-urgent);
}

.bellNote {
  margin: 0;
  padding: 16px 18px;
  color: var(--text-secondary);
  font-size: 13px;
}

.bellNotice {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  border-radius: 4px;
}

.bellNotice .bellItem {
  flex: 1 1 auto;
  min-width: 0;
  padding-left: 22px;
}

.bellUnread {
  background: var(--brand-cyan-50);
}

.bellItemBody {
  color: var(--text-body);
  font-size: 13px;
  line-height: 1.4;
}

.unreadDot {
  position: absolute;
  top: 16px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--brand-cyan);
}

.bellDismiss {
  display: grid;
  flex: none;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
}

.bellDismiss[data-hovered] {
  background: var(--gray-75);
  color: var(--text-heading);
}
